"use client";

import { useMemo, useState } from "react";
import AddToCartButton from "@/components/cart/AddToCartButton";
import IconSymbolTaka from "@/components/icons/icon-symbol-taka";

type FilterItem = { name: string; description?: string; price?: number };

type MenuAPI = {
  id: number;
  name: string;
  description?: string;
  price: number | string;
  imageUrl?: string;
  category: number;
  available: boolean;
  filters?: FilterItem[] | null;
};

/** ---------- Utils (client) ---------- **/
const toNumber = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normFilters = (val?: any): FilterItem[] => {
  if (!val) return [];
  if (!Array.isArray(val)) return [];
  return val
    .map((x) => {
      if (!x || typeof x !== "object") return null;
      const name = String(x.name ?? "").trim();
      if (!name) return null;
      const description =
        x.description !== undefined && x.description !== null
          ? String(x.description)
          : undefined;
      const price =
        x.price !== undefined && x.price !== null
          ? toNumber(x.price)
          : undefined;
      return { name, description, price };
    })
    .filter(Boolean) as FilterItem[];
};

const formatMoney = (n: number) => `৳ ${n.toFixed(2).replace(/\.00$/, "")}`;

export default function DetailsClient({ item }: { item: MenuAPI }) {
  const basePrice = toNumber(item.price);
  const filters = useMemo(() => normFilters(item.filters), [item.filters]);

  // default: base (no filter selected)
  const [selected, setSelected] = useState<string | null>(null);

  const selectedFilter = useMemo(
    () => filters.find((f) => f.name === selected),
    [filters, selected]
  );

  const displayPrice = selectedFilter?.price ?? basePrice;

  // Pass effective price & optional filter name into cart
  const cartItem = {
    id: item.id,
    name: item.name,
    price: displayPrice,
    imageUrl: item.imageUrl,
    filter: selectedFilter?.name, // optional; undefined => base
  } as any;

  return (
    <div className="center flex-col items-start">
      <h1 className="text-[33px] sm:text-4xl xl:text-5xl 2xl:text-[58.05px] md:mb-5 sm:mb-2 text-old-lace font-young-serif">
        {item.name}
      </h1>
      <p className="max-lg:max-w-[500px] text-sm sm:text-base md:text-lg xl:text-xl 2xl:text-[29.02px] text-old-lace">
        {item.description || "—"}
      </p>

      {/* Filters list (optional) */}
      {filters.length > 0 && (
        <div className="mt-4 w-full">
          <h3 className="text-base sm:text-lg font-semibold mb-2 text-old-lace">
            Choose an option (optional)
          </h3>
          <div className="space-y-2">
            {/* None → base */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="variant"
                checked={selected === null}
                onChange={() => setSelected(null)}
                className="mt-1 accent-old-gold"
              />
              <span className="text-old-lace">
                <span className="font-medium">Base</span>{" "}
                <span className="opacity-80">• {formatMoney(basePrice)}</span>
              </span>
            </label>

            {filters.map((f) => (
              <label
                key={f.name}
                className="flex items-start gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="variant"
                  checked={selected === f.name}
                  onChange={() => setSelected(f.name)}
                  className="mt-1 accent-old-gold"
                />
                <span className="text-old-lace">
                  <span className="font-medium">{f.name}</span>{" "}
                  {f.description ? (
                    <span className="opacity-70">• {f.description}</span>
                  ) : null}
                  {typeof f.price === "number" ? (
                    <span className="opacity-90">
                      {" "}
                      • {formatMoney(f.price)}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <p className="my-5 xl:my-10 2xl:my-[73px] text-4xl xl:text-5xl 2xl:text-[65.06px] text-white flex items-center gap-4">
        <IconSymbolTaka className="xl:w-[36px] w-6" />
        {displayPrice.toFixed(2)}
      </p>

      <div className="center justify-start flex-row gap-2.5 2xl:gap-6.5">
        <AddToCartButton
          item={cartItem}
          className="sm:max-md:max-w-[368px] w-full center h-10 sm:h-12 xl:h-[52px] 2xl:h-[67px] px-[35px] border border-white bg-white/[1%] backdrop-blur-[23.55px] text-white text-xs sm:text-lg 2xl:text-[22px] rounded-[25px] flex items-center gap-2 overflow-hidden"
        />
      </div>
    </div>
  );
}
